import React, { useState, useMemo } from 'react'
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  useTheme,
  alpha,
  Toolbar,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  TableChart as TableChartIcon,
  Support as SupportIcon,
  BugReport as BugReportIcon,
  Upgrade as UpgradeIcon
} from '@mui/icons-material'
import { DashboardAnalyzer } from './dashboardAnalyzer'
import { TicketVisualizer } from './ticketVisualizer'
import { constants } from '../../config'
import { getDrawerTicketCountsBySupportType } from '../../utils/ticketDrawerData'
import { DRAWER_TOGGLE_Z, DRAWER_TOGGLE_BELOW_BAR_Z } from './dashboardLayerZ'

const { graphFilters } = constants

/** Matches drawer paper width so the flyout tab sits flush with the drawer edge (avoids gap when `maxWidth` caps width below `75vw`). */
const DATA_DRAWER_WIDTH_SM = 'min(75vw, 1200px)'

const TAB_FONT = '0.875rem'
const TITLE_FONT = '0.9375rem'
const TAB_LINE = 1.3

const formatDrawerTypePct = (n) => {
  const v = Number(n)
  if (!Number.isFinite(v)) return '0.0'
  return v.toFixed(1)
}

const DataDrawer = ({
  props: viewProps = {},
  controls = {}
}) => {
  const theme = useTheme()
  const dd = theme.custom.colors.dataDrawer
  const [open, setOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  const drawerAccent = useMemo(() => {
    if (activeTab === 0) return theme.custom.keysys.blue
    if (activeTab === 1) return dd.tabIconTickets
    if (activeTab === 2) return dd.tabIconVuln
    return dd.tabIconUpgrades
  }, [activeTab, theme.custom.keysys.blue, dd.tabIconTickets, dd.tabIconVuln, dd.tabIconUpgrades])
  const [chartTableGroupBy, setChartTableGroupBy] = useState('client')
  // Inner drawer tabs: 0 chart, 1 support, 2 vuln, 3 upgrades
  const ticketType = activeTab === 1 ? graphFilters.SUPPORT : activeTab === 2 ? graphFilters.VULNERABILITIES : activeTab === 3 ? graphFilters.UPGRADES : null

  const {
    graphStatistics = {},
    activeTab: mainChartTab = '',
    graphData = {},
    projectData = {},
    filtersSelected = {},
    dataTranspositionOpen = false
  } = viewProps

  const flyoutZIndex =
    dataTranspositionOpen && !open ? DRAWER_TOGGLE_BELOW_BAR_Z : DRAWER_TOGGLE_Z

  const drawerTicketCounts = useMemo(
    () => getDrawerTicketCountsBySupportType(viewProps),
    [graphData, mainChartTab, projectData, filtersSelected]
  )

  const typePercents = useMemo(() => {
    const p = graphStatistics?.[mainChartTab]?.UnitsPercentages
    const cS = drawerTicketCounts[graphFilters.SUPPORT]
    const cV = drawerTicketCounts[graphFilters.VULNERABILITIES]
    const cU = drawerTicketCounts[graphFilters.UPGRADES]
    const totalC = cS + cV + cU
    if (p && typeof p.supportPct === 'number') {
      return {
        support: p.supportPct,
        vulnerabilities: p.vulnerabilitiesPct,
        upgrades: p.upgradesPct
      }
    }
    if (totalC <= 0) {
      return { support: 0, vulnerabilities: 0, upgrades: 0 }
    }
    return {
      support: (cS / totalC) * 100,
      vulnerabilities: (cV / totalC) * 100,
      upgrades: (cU / totalC) * 100
    }
  }, [graphStatistics, mainChartTab, drawerTicketCounts])

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const tabsSx = useMemo(
    () => ({
      width: '100%',
      maxWidth: { xs: '100%', sm: 720 },
      minHeight: 52,
      '& .MuiTab-root': {
        minHeight: 52,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: TAB_FONT,
        lineHeight: TAB_LINE,
        color: dd.muted,
        borderRadius: 1,
        py: 0.75,
        '&.Mui-selected': {
          color: drawerAccent,
          backgroundColor: alpha(drawerAccent, 0.12)
        }
      },
      '& .MuiTabs-indicator': {
        backgroundColor: drawerAccent,
        height: 3,
        borderRadius: 2
      },
      '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 }
    }),
    [dd.muted, drawerAccent]
  )

  const ticketTabLabel = (title, pctValue, count, accentHex) => {
    const badgeFg = accentHex === dd.tabIconVuln ? '#fff' : alpha('#000', 0.87)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
            gap: 0.125
          }}
        >
          <Typography
            component='span'
            variant='inherit'
            sx={{ fontWeight: 600, fontSize: TAB_FONT, lineHeight: TAB_LINE, color: 'inherit' }}
          >
            {title}
          </Typography>
          <Typography
            component='span'
            variant='inherit'
            sx={{ fontWeight: 700, fontSize: TAB_FONT, lineHeight: TAB_LINE, color: 'inherit' }}
          >
            {formatDrawerTypePct(pctValue)}%
          </Typography>
        </Box>
        {count > 0 && (
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 22,
              height: 22,
              px: 0.5,
              borderRadius: '11px',
              fontSize: TAB_FONT,
              fontWeight: 800,
              lineHeight: 1,
              backgroundColor: accentHex,
              color: badgeFg,
              flexShrink: 0
            }}
          >
            {count}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: {
            xs: open ? 'calc(100vw - 48px)' : 0,
            sm: open ? DATA_DRAWER_WIDTH_SM : 0
          },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: flyoutZIndex,
          transition: 'left 0.3s ease-in-out'
        }}
      >
        <IconButton
          onClick={toggleDrawer}
          aria-label={open ? 'Close drawer' : 'Open data drawer'}
          sx={{
            backgroundColor: alpha(drawerAccent, 0.92),
            color: dd.text,
            borderRadius: '0 8px 8px 0',
            width: 48,
            height: 56,
            minWidth: 48,
            minHeight: 48,
            boxShadow: `0 4px 14px ${alpha(drawerAccent, 0.45)}`,
            '&:hover': {
              backgroundColor: drawerAccent,
              boxShadow: `0 6px 18px ${alpha(drawerAccent, 0.55)}`
            },
            '@media (min-width: 600px)': { height: 64 }
          }}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Drawer
        anchor='left'
        open={open}
        variant='persistent'
        sx={{
          width: open ? { xs: '100vw', sm: DATA_DRAWER_WIDTH_SM } : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: DATA_DRAWER_WIDTH_SM },
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: dd.background,
            backgroundImage: `linear-gradient(195deg, ${alpha(drawerAccent, 0.16)} 0%, transparent 48%)`,
            borderRight: `1px solid ${alpha(drawerAccent, 0.45)}`,
            boxShadow: `12px 0 40px ${alpha('#000', 0.55)}`
          }
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 'auto', sm: '72px !important' },
            flexShrink: 0,
            background: `linear-gradient(90deg, ${alpha(drawerAccent, 0.22)} 0%, ${alpha(dd.surface, 1)} 100%)`,
            borderBottom: `1px solid ${alpha(drawerAccent, 0.38)}`,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            gap: 1.25,
            rowGap: 1.25
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexShrink: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(drawerAccent, 0.22),
                border: `1px solid ${alpha(drawerAccent, 0.48)}`,
                flexShrink: 0
              }}
            >
              {activeTab === 0 && <TableChartIcon sx={{ color: dd.tabIconChart, fontSize: 24 }} />}
              {activeTab === 1 && <SupportIcon sx={{ color: dd.tabIconTickets, fontSize: 24 }} />}
              {activeTab === 2 && <BugReportIcon sx={{ color: dd.tabIconVuln, fontSize: 24 }} />}
              {activeTab === 3 && <UpgradeIcon sx={{ color: dd.tabIconUpgrades, fontSize: 24 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: TITLE_FONT,
                  lineHeight: TAB_LINE,
                  color: dd.text
                }}
              >
                {activeTab === 0 ? 'Chart Data' : activeTab === 1 ? 'Tickets' : activeTab === 2 ? 'Vulnerabilities' : 'Upgrades'}
              </Typography>
              <Typography sx={{ color: dd.muted, fontSize: TAB_FONT, lineHeight: TAB_LINE, fontWeight: 500 }}>
                {activeTab === 0 ? 'Same series as the main chart' : 'Tickets in the current graph & filters'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: { xs: 0, sm: 1 }, minWidth: { xs: 0, sm: 8 } }} />

          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: { xs: 'stretch', sm: 'center' },
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 'none' },
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              pl: { xs: 0, sm: 2 },
              borderLeft: { xs: 'none', sm: `1px solid ${alpha(drawerAccent, 0.3)}` }
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile
              sx={tabsSx}
            >
              <Tab
                label='Chart Data'
                icon={<TableChartIcon sx={{ fontSize: 22, color: dd.tabIconChart }} />}
                iconPosition='start'
              />
              <Tab
                label={ticketTabLabel(
                  'Tickets',
                  typePercents.support,
                  drawerTicketCounts[graphFilters.SUPPORT],
                  dd.tabIconTickets
                )}
                icon={<SupportIcon sx={{ fontSize: 22, color: dd.tabIconTickets }} />}
                iconPosition='start'
              />
              <Tab
                label={ticketTabLabel(
                  'Vulnerabilities',
                  typePercents.vulnerabilities,
                  drawerTicketCounts[graphFilters.VULNERABILITIES],
                  dd.tabIconVuln
                )}
                icon={<BugReportIcon sx={{ fontSize: 22, color: dd.tabIconVuln }} />}
                iconPosition='start'
              />
              <Tab
                label={ticketTabLabel(
                  'Upgrades',
                  typePercents.upgrades,
                  drawerTicketCounts[graphFilters.UPGRADES],
                  dd.tabIconUpgrades
                )}
                icon={<UpgradeIcon sx={{ fontSize: 22, color: dd.tabIconUpgrades }} />}
                iconPosition='start'
              />
            </Tabs>
          </Box>

          <Box sx={{ flex: { xs: 0, sm: 1 }, minWidth: { xs: 0, sm: 8 } }} />

          {activeTab === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                flexShrink: 0,
                pl: { xs: 0, sm: 2 },
                borderLeft: { xs: 'none', sm: `1px solid ${alpha(drawerAccent, 0.3)}` }
              }}
            >
              <Typography
                sx={{ color: dd.muted, fontWeight: 700, letterSpacing: 0.3, fontSize: TAB_FONT, lineHeight: TAB_LINE }}
              >
                Group by
              </Typography>
              <ToggleButtonGroup
                exclusive
                size='medium'
                value={chartTableGroupBy}
                onChange={(e, v) => v && setChartTableGroupBy(v)}
                sx={{ flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}
              >
                <ToggleButton
                  value='client'
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: TAB_FONT,
                    px: 1.5,
                    py: 0.75,
                    '&.Mui-selected': {
                      color: drawerAccent,
                      backgroundColor: alpha(drawerAccent, 0.14),
                      borderColor: alpha(drawerAccent, 0.45)
                    }
                  }}
                >
                  Client
                </ToggleButton>
                <ToggleButton
                  value='month'
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: TAB_FONT,
                    px: 1.5,
                    py: 0.75,
                    '&.Mui-selected': {
                      color: drawerAccent,
                      backgroundColor: alpha(drawerAccent, 0.14),
                      borderColor: alpha(drawerAccent, 0.45)
                    }
                  }}
                >
                  Month
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <IconButton
            onClick={toggleDrawer}
            size='small'
            sx={{
              color: dd.text,
              backgroundColor: alpha('#fff', 0.06),
              flexShrink: 0,
              alignSelf: 'center',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.12)
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Toolbar>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 'min(62vh, 640px)', sm: 'min(58vh, 680px)' },
            overflowY: 'auto',
            p: 2,
            backgroundColor: dd.background
          }}
        >
          {activeTab === 0
            ? (
              <DashboardAnalyzer
                props={viewProps}
                controls={{
                  ...controls,
                  chartTableGroupBy,
                  setChartTableGroupBy
                }}
                mode='drawer'
              />)
            : ticketType
              ? (
                <TicketVisualizer props={viewProps} supportTypeFilter={ticketType} />)
              : null}
        </Box>
      </Drawer>
    </>
  )
}

export { DataDrawer }
